import React, { useContext, useEffect, useState } from "react";
import {
  Box,
  Flex,
  Icon,
  useColorModeValue,
  Link,
  Drawer,
  DrawerContent,
  useDisclosure,
  BoxProps,
  FlexProps,
  Image,
  useTheme,
  DrawerOverlay,
  useBreakpointValue,
  CloseButton,
} from "@chakra-ui/react";
import { IconType } from "react-icons";
import {
  FaUserAlt,
  FaCheck,
  FaClock,
  FaFileAlt,
  FaChartPie,
  FaClone,
} from "react-icons/fa";
import { AppContext } from "AppProvider/AppContext";
import { routePath } from "config/pathConstant";
import { HiDocument } from "react-icons/hi";

interface LinkItemProps {
  name: string;
  icon: IconType;
  role: string;
  url: string;
}

// Organisation sidebar
const LinkData: Array<LinkItemProps> = [
  {
    name: "Dashboard",
    icon: FaChartPie,
    role: "Dashboard",
    url: "#",
  },
  {
    name: routePath.Approvals,
    icon: FaClock,
    role: "Dashboard",
    url: "#",
  },
  {
    name: routePath.duplicate,
    icon: FaClone,
    role: "Dashboard",
    url: "#",
  },
  {
    name: routePath.initiateDocument,
    icon: HiDocument,
    role: "Dashboard",
    url: "#",
  },
  {
    name: routePath.extractor,
    icon: FaFileAlt,
    role: "Dashboard",
    url: "#",
  },
];

// Vendor sidebar
const AdminLinkItems: Array<LinkItemProps> = [
  {
    name: "Users",
    icon: FaUserAlt,
    role: "User",
    url: "#",
  },
  {
    name: "Foreign Estimate",
    icon: FaChartPie,
    role: "User",
    url: "#",
  },
  {
    name: routePath.CostCenter,
    icon: FaClone,
    role: "User",
    url: "#",
  },
  {
    name: routePath.PrFiUserMapping,
    icon: FaClone,
    role: "User",
    url: "#",
  },
  {
    name: routePath.ForeignBankMaster,
    icon: FaClone,
    role: "User",
    url: "#",
  },
  {
    name: routePath.sapXMLPayload,
    icon: FaCheck,
    role: "User",
    url: "#",
  },
  {
    name: routePath.foreignAdminTools,
    icon: FaCheck,
    role: "User",
    url: "#",
  },
  {
    name: routePath.emergingRegion,
    icon: FaCheck,
    role: "User",
    url: "#",
  },
  {
    name: routePath.miroDocuments,
    icon: HiDocument,
    role: "User",
    url: "#",
  },
  {
    name: routePath.poMiroUser,
    icon: HiDocument,
    role: "User",
    url: "#",
  },
  {
    name: routePath.VendorPaymentTerm,
    icon: FaClone,
    role: "User",
    url: "#",
  },
];

type setActivePage = React.Dispatch<React.SetStateAction<string>>;

type Props = {
  activePage: string;
  setActivePage: setActivePage;
  openMobileSideDrawer: boolean;
  setOpenMobileSideDrawer: any;
};

export default function Sidebar({
  activePage,
  setActivePage,
  openMobileSideDrawer,
  setOpenMobileSideDrawer,
}: Props) {
  const { isOpen, onClose } = useDisclosure();
  const [hideName, setHideName] = useState(true);

  return (
    <>
      {/* for desktop  */}
      <Box
        minH="100vh"
        bg={useColorModeValue("gray.1000", "gray.900")}
        minW={hideName ? "5rem" : "15rem"}
        w={hideName ? "0.5%" : "13%"}
        // transition="all 0.8s ease"
        onMouseOver={() => setHideName(false)}
        onMouseLeave={() => setHideName(true)}
        display={{ base: "none", lg: "block" }}
        style={{ position: "fixed", zIndex: 10 }}
      >
        <SidebarContent
          onClose={() => onClose()}
          display={{ base: "none", lg: "block" }}
          activePage={activePage}
          setActivePage={setActivePage}
          hideName={hideName}
          setHideName={setHideName}
        />
        <Drawer
          autoFocus={false}
          isOpen={isOpen}
          placement="left"
          onClose={onClose}
          returnFocusOnClose={false}
          onOverlayClick={onClose}
          size="full"
        >
          <DrawerContent>
            <SidebarContent
              onClose={onClose}
              activePage={activePage}
              setActivePage={setActivePage}
              hideName={hideName}
              setHideName={setHideName}
            />
          </DrawerContent>
        </Drawer>
        {/* <MobileNav display={{ base: "flex", md: "none" }} onOpen={onOpen} /> */}
      </Box>

      {/* for Mobile only */}
      <Box display={{ base: "block", lg: "none" }}>
        <Drawer
          isOpen={openMobileSideDrawer}
          placement="left"
          onClose={setOpenMobileSideDrawer}
        // onOverlayClick={onClose}
        // finalFocusRef={btnRef}
        >
          <DrawerOverlay />
          <DrawerContent w="20% !important">
            <SidebarContent
              onClose={() => onClose}
              activePage={activePage}
              setActivePage={setActivePage}
              hideName={true}
              setHideName={setHideName}
              setOpenMobileSideDrawer={setOpenMobileSideDrawer}
            />
          </DrawerContent>
        </Drawer>
      </Box>
    </>
  );
}

interface SidebarProps extends BoxProps {
  onClose: () => void;
  activePage: string;
  setActivePage: setActivePage;
  hideName: boolean;
  setOpenMobileSideDrawer?: any;
  setHideName: React.Dispatch<React.SetStateAction<boolean>>;
}

const SidebarContent = ({
  onClose,
  activePage,
  setActivePage,
  hideName,
  setHideName,
  setOpenMobileSideDrawer,
  ...rest
}: SidebarProps) => {
  const { accountDetails, workFlowData, getAccountDetails, activeWorkflow } =
    useContext(AppContext);
  const [LinkItems, setLinkItems] = useState<LinkItemProps[]>(LinkData);
  const mobileSidebar = useBreakpointValue({ lg: true });

  useEffect(() => {
    if (accountDetails) {
      if (accountDetails?.allowedTabs?.length > 0) {
        const newLinks: LinkItemProps[] = [];

        if (accountDetails.allowedTabs.includes("Initiate Payment")) {
          newLinks.push({
            name: "Initiate Payment",
            icon: FaClock,
            role: "Dashboard",
            url: "#",
          });
        }

        if (accountDetails.allowedTabs.includes(routePath.sapXMLPayload)) {
          newLinks.push({
            name: routePath.sapXMLPayload,
            icon: FaCheck,
            role: "Dashboard",
            url: "#",
          });
        }

        if (accountDetails.allowedTabs.includes(routePath.TaxCodes)) {
          newLinks.push({
            name: routePath.TaxCodes,
            icon: FaClone,
            role: "Dashboard",
            url: "#",
          });
        }
        if (accountDetails.allowedTabs.includes(routePath.WthTaxs)) {
          newLinks.push({
            name: routePath.WthTaxs,
            icon: FaClone,
            role: "Dashboard",
            url: "#",
          });
        }

        if (accountDetails.allowedTabs.includes(routePath.FastForwardingVendorMaster)) {
          newLinks.push({
            name: routePath.FastForwardingVendorMaster,
            icon: FaClone,
            role: "Dashboard",
            url: "#",
          });
        }

        if (accountDetails.allowedTabs.includes(routePath.CostCenter)) {
          newLinks.push({
            name: routePath.CostCenter,
            icon: FaClone,
            role: "Dashboard",
            url: "#",
          });
        }

        if (accountDetails.allowedTabs.includes(routePath.ForeignBankMaster)) {
          newLinks.push({
            name: routePath.ForeignBankMaster,
            icon: FaClone,
            role: "Dashboard",
            url: "#",
          });
        }

        if (accountDetails.allowedTabs.includes(routePath.foreignDocumentUpload)) {
          newLinks.push({
            name: routePath.foreignDocumentUpload,
            icon: FaFileAlt,
            role: "Dashboard",
            url: "#",
          });
        }

        if (accountDetails.allowedTabs.includes(routePath.VendorPaymentTerm)) {
          newLinks.push({
            name: routePath.VendorPaymentTerm,
            icon: FaClone,
            role: "Dashboard",
            url: "#",
          });
        }
        
        const uniqueLinks = newLinks.filter(
          (link, index, self) =>
            index === self.findIndex((l) => l.name === link.name),
        );
        setLinkItems(uniqueLinks);
      } else {
        if (
          (accountDetails?.level === "level-1" ||
            accountDetails?.level === "admin") &&
          workFlowData?.processName === "foreign" &&
          workFlowData?.processType === "NonPo"
        ) {
          setLinkItems((prev: LinkItemProps[]) => {
            const alreadyExists = prev.some(
              (item) => item.name === routePath.fileMasterData,
            );

            if (alreadyExists) return prev;

            return [
              ...prev,
              {
                name: routePath.fileMasterData,
                icon: FaClone,
                role: "Dashboard",
                url: "#",
              },
              ...(accountDetails?.level === "admin"
                ? [
                  {
                    name: routePath.paymentInitiateDocument,
                    icon: FaClone,
                    role: "Dashboard",
                    url: "#",
                  },
                ]
                : []),
              {
                name: routePath.foreignDocumentUpload,
                icon: FaFileAlt,
                role: "Dashboard",
                url: "#",
              },
            ];
          });
        } else {
          if (activeWorkflow !== workFlowData?._id) {
            let linkDatas: any = LinkData.filter(
              (it: any) => it.name !== routePath.sapXMLPayload,
            );

            const uniqueLinks = linkDatas.filter(
              (link: any, index: number, self: any) =>
                index === self.findIndex((l: any) => l.name === link.name),
            );
            setLinkItems(uniqueLinks);
          }
        }
      }
    }
  }, [activeWorkflow, accountDetails]);

  useEffect(() => {
    if (
      accountDetails &&
      (accountDetails?.role === "admin" || accountDetails?.level === "admin")
    ) {
      if (
        LinkItems.filter((item: LinkItemProps) => item.name === "WorkFlow")
          .length === 0
      ) {
        setLinkItems((prev: LinkItemProps[]) => [
          ...prev,
          {
            name: "WorkFlow",
            icon: FaCheck,
            role: "WorkFlow",
            url: "#",
          },
        ]);
      }
    }
  }, [accountDetails]);

  // useEffect(() => {
  //   if (
  //     accountDetails &&
  //     accountDetails?.role === "admin" &&
  //     workFlowData &&
  //     workFlowData?.onboarding
  //   ) {
  //     if (
  //       LinkItems.filter((item: LinkItemProps) => item.name === "Onboarding")
  //         .length === 0
  //     ) {
  //       setLinkItems((prev: LinkItemProps[]) => [
  //         ...prev,
  //         {
  //           name: "Onboarding",
  //           icon: FaClock,
  //           role: "Dashboard",
  //           url: "#",
  //         },
  //       ]);
  //     }
  //     if (
  //       LinkItems.filter((item: LinkItemProps) => item.name === "Form")
  //         .length === 0
  //     ) {
  //       setLinkItems((prev: LinkItemProps[]) => [
  //         ...prev,
  //         {
  //           name: "Form",
  //           icon: FaFile,
  //           role: "Dashboard",
  //           url: "#",
  //         },
  //       ]);
  //     }
  //   }
  // }, [workFlowData]);

  return (
    <Box
      bg={useColorModeValue("gray.1000", "gray.500")}
      zIndex={10}
      borderRight="1px"
      borderRightColor={useColorModeValue("gray.200", "gray.700")}
      pos="fixed"
      h="full"
      {...rest}
    // boxShadow='md'
    >
      <Flex h="20" alignItems="center" justifyContent="space-between" px="5">
        <Flex alignItems="center">
          <Image src={"/img/short-logo.png"} w={50} />
          {!hideName && (
            <Box fontSize="md" ml={3} fontWeight={700} color="bluess.800">
              {"WorkFlow"}
            </Box>
          )}
          {/* For the Mobile only */}
          {!mobileSidebar && (
            <Box fontSize="md" ml={3} fontWeight={700} color="bluess.800">
              {"WorkFlow"}
            </Box>
          )}
        </Flex>
        <CloseButton
          display={{ lg: "none" }}
          onClick={() => {
            if (setOpenMobileSideDrawer) {
              setOpenMobileSideDrawer(false);
            }
          }}
        />
      </Flex>
      <Box px="1rem" mb=".1rem">
        <Box
          h="2px"
          w="100%"
          backgroundImage="linear-gradient(to right, rgba(224, 225, 226, 0) 0% , #E0E1E2 50%, rgba(224, 225, 226, 0.1563) 100%)"
        />
      </Box>

      <Box
        overflowY="auto"
        h="calc(100vh - 6rem)"
        className="customScrollBar"
        pt={5}
      >
        {LinkItems
          // hide duplicate for mmt and foreign
          .filter((link) => {
            if (
              (workFlowData.processName === "mmt" ||
                workFlowData.processName === "foreign") &&
              link.name === routePath.duplicate
            ) {
              return false;
            }
            if (
              workFlowData.processName === "foreign" &&
              (link.name === routePath.initiateDocument || link.name === routePath.foreignDocumentUpload)
            ) {
              return false;
            }
            return true;
          })
          .map((link) => (
            <NavItem
              key={link.name}
              name={link.name}
              active={link.name === activePage}
              icon={link.icon}
              url={link.url}
              setActivePage={setActivePage}
              hideName={
                mobileSidebar === undefined
                  ? false
                  : hideName === true
                    ? true
                    : false
              }
              setHideName={setHideName}
              setOpenMobileSideDrawer={setOpenMobileSideDrawer}
            >
              {link.name === "Approvals"
                ? workFlowData.processName === "foreign"
                  ? "Approvals"
                  : "Approvals"
                : link.name === routePath.FastForwardingVendorMaster
                ? "FF Vendor Master"
                : link.name}
            </NavItem>
          ))}

        {/* ALWAYS SHOW VENDOR & SAA DETAILS (NO CONDITIONS) */}
        <NavItem
          key={routePath.vendorSaaDetails}
          name={routePath.vendorSaaDetails}
          active={routePath.vendorSaaDetails === activePage}
          icon={FaFileAlt}
          url="#"
          setActivePage={setActivePage}
          hideName={
            mobileSidebar === undefined
              ? false
              : hideName === true
                ? true
                : false
          }
          setHideName={setHideName}
          setOpenMobileSideDrawer={setOpenMobileSideDrawer}
        >
          {routePath.vendorSaaDetails}
        </NavItem>
        
        {workFlowData &&
          workFlowData?.tab &&
          workFlowData?.tab?.map(
            (item: any) =>
              item.isActive === true &&
              (!accountDetails?.allowWithoutWorkflow ||
                !accountDetails?.allowedTabs ||
                accountDetails?.allowedTabs.includes(item.label)) && (
                <NavItem
                  key={item.label}
                  name={item.label}
                  active={item.label === activePage}
                  icon={FaFileAlt}
                  url="#"
                  setActivePage={setActivePage}
                  hideName={
                    mobileSidebar === undefined
                      ? false
                      : hideName === true
                        ? true
                        : false
                  }
                  setHideName={setHideName}
                  setOpenMobileSideDrawer={setOpenMobileSideDrawer}
                >
                  {item.label}
                </NavItem>
              ),
          )}
        <Box px="1rem" mb=".7rem">
          <Box
            h="2px"
            w="100%"
            backgroundImage="linear-gradient(to right, rgba(224, 225, 226, 0) 0% , #E0E1E2 50%, rgba(224, 225, 226, 0.1563) 100%)"
          />
        </Box>

        {/* Display User and User Roles */}

        {AdminLinkItems.filter((link) => {
          if (
            // workFlowData?.processName === "foreign" &&
            (link.name === "Foreign Estimate" ||
              link.name === routePath.ForeignBankMaster ||
              link.name === routePath.sapXMLPayload)
          ) {
            return false; // Hidden only for foreign admin tools
          }
          if (link.name === routePath.CostCenter || link.name === routePath.PrFiUserMapping) {
            return workFlowData?.processName === "pr";
          }
          if (link.name === routePath.VendorPaymentTerm) {
            return workFlowData?.processName === "domestic";
          }
          if (link.name === routePath.foreignAdminTools) {
            return workFlowData?.processName === "foreign";
          }
          if (link.name === routePath.emergingRegion) {
            return workFlowData?.processName === "emerging";
          }
          if (link.name === routePath.poMiroUser) {
            const proc = workFlowData?.processName?.toLowerCase();
            return proc === "domestic" || proc === "po";
          }
          if (link.name === routePath.miroDocuments) {
            const proc = workFlowData?.processName?.toLowerCase();
            return proc === "domestic" || proc === "po";
          }
          return true; // Keep "Users" and others
        }).map(
          (link) =>
            // companyType === "vendor" &&
            accountDetails?.level === "admin" &&
            (!accountDetails?.allowWithoutWorkflow ||
              !accountDetails?.allowedTabs ||
              accountDetails?.allowedTabs?.includes(link.name)) && (
              <NavItem
                key={link.name}
                name={link.name}
                active={link.name === activePage}
                icon={link.icon}
                url={link.url}
                setActivePage={setActivePage}
                hideName={
                  mobileSidebar === undefined
                    ? false
                    : hideName === true
                      ? true
                      : false
                }
                setHideName={setHideName}
                setOpenMobileSideDrawer={setOpenMobileSideDrawer}
              >
                {link.name === "Foreign Estimate" ? "Foreign Exchange Rate" : link.name}
              </NavItem>
            ),
        )}
      </Box>
    </Box>
  );
};

interface NavItemProps extends FlexProps {
  name: string;
  icon: IconType;
  url?: string;
  children: string | number;
  active: boolean;
  setActivePage: setActivePage;
  hideName: boolean;
  setOpenMobileSideDrawer?: any;
  setHideName: React.Dispatch<React.SetStateAction<boolean>>;
}

const NavItem = ({
  name,
  icon,
  active,
  setActivePage,
  hideName,
  setHideName,
  setOpenMobileSideDrawer,
  url,
  children,
  ...rest
}: NavItemProps) => {
  const theme = useTheme();
  const mobileSidebar = useBreakpointValue({ lg: true });

  return (
    <Link
      href="#"
      textDecoration="none"
      _focus={{ boxShadow: "none" }}
      _hover={{ textDecoration: "none" }}
      onClick={() => {
        // for the mobile on select the tab , drawer will close
        if (mobileSidebar === undefined && setOpenMobileSideDrawer) {
          setOpenMobileSideDrawer(false);
        }
        setActivePage(name);
      }}
    >
      <Flex
        {...(active
          ? { "data-active": true }
          : null)} /* Added to make the link active */
        align="center"
        p=".7rem"
        mx="4"
        borderRadius="1rem"
        role="group"
        cursor="pointer"
        color="gray.400"
        fontWeight="700"
        fontSize={{ base: "sm", lg: "md" }}
        w={hideName ? "3.7rem" : "14.9rem"}
        whiteSpace="nowrap"
        // transition="all .8s"
        mb={2}
        _hover={{
          bg: "white",
          color: "gray.900",
          fontWeight: "700",
          ml: "13px",
          textDecoration: "none",
        }}
        _active={{
          bg: "white",
          color: "gray.900",
          fontWeight: "700",
          ml: "13px",
          boxShadow: `0 0 2px 2px ${theme.colors.gray["300"]}`,
        }}
        {...rest}
      >
        {icon && (
          <Box
            {...(active
              ? { "data-active": true }
              : null)} /* Added to make the link active */
            mr="2"
            bg="white"
            h="30px"
            w="30px"
            p="6px"
            m={hideName ? "auto" : "0 7.5px 0 5px"}
            borderRadius=".7rem"
            // transition="all .8s"
            _groupHover={{ bg: "teal.400" }}
            _active={{
              bg: "teal.400",
            }}
          >
            <Icon
              {...(active
                ? { "data-active": true }
                : null)} /* Added to make the link active */
              fontSize="1.1rem"
              color="teal.400"
              _groupHover={{
                color: "white",
              }}
              _active={{
                color: "white",
              }}
              as={icon}
            />
          </Box>
        )}
        {!hideName && children}
      </Flex>
    </Link>
  );
};